import { useMemo } from "react";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { Ico } from "../../../components/icons/icons.jsx";
import { DeleteConfirmModal } from "../../../components/modal/DeleteConfirmModal.jsx";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm.js";
import { buildAccountDetailTokens, detailActionBtnStyle } from "../accountDetailTheme.js";
import { buildAssetAppMetaFields, buildAssetDetailFields, buildAssetDetailRows, deriveAssetDisplayName } from "../assetUtils.js";
import { AssetCommandsSection } from "./AssetCommandsSection.jsx";

const ICON_MAP = {
  model: Ico.model,
  layers: Ico.layers,
  device: Ico.device,
  grid: Ico.grid,
};

export function AssetDetailPanel({ asset, assetType, onEdit, onDelete, commandState }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const ap = useMemo(() => buildAccountDetailTokens(dark, c, A), [dark, c, A]);
  const { requestDelete, deleteModalProps } = useDeleteConfirm();

  if (!asset) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: c.sub, fontSize: 13, background: c.bg }}>
        Select an asset to view details
      </div>
    );
  }

  const detailFields = buildAssetDetailFields(asset, assetType);
  const detailRows = buildAssetDetailRows(detailFields);
  const appMetaFields = buildAssetAppMetaFields(asset, assetType);
  const displayName = deriveAssetDisplayName(asset, assetType);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, background: c.bg }}>
      <div style={{ padding: "14px 16px 16px", overflow: "auto", minHeight: 0, flex: 1 }}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: A, marginBottom: 4 }}>
            Overview
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>Details</div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
            padding: "12px 16px",
            borderRadius: 10,
            background: c.hover,
            border: `1px solid ${c.cardBdr}`,
            marginBottom: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: c.text, lineHeight: 1.3 }}>{assetType?.name ?? "—"}</div>
            {appMetaFields.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                {appMetaFields.map((field) => (
                  <div key={field.label} style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: c.sub, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {field.label}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{field.value || "—"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: c.sub, marginTop: 4 }}>{displayName}</div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button type="button" onClick={() => onEdit(asset)} style={detailActionBtnStyle(ap)} aria-label="Edit asset">
              {Ico.pencil}
            </button>
            <button
              type="button"
              onClick={() => requestDelete({
                title: "Delete asset",
                description: "This asset will be permanently removed from the account.",
                confirmText: displayName,
                entityLabel: "Asset",
                onConfirm: () => onDelete(asset.id),
              })}
              style={detailActionBtnStyle(ap, "danger")}
              aria-label="Delete asset"
            >
              {Ico.trash}
            </button>
          </div>
        </div>

        <div style={{ background: ap.card, border: `1px solid ${ap.cardBorder}`, borderRadius: 10, overflow: "hidden" }}>
          {detailRows.map((row, rowIdx) => (
            <div
              key={rowIdx}
              style={{
                display: "grid",
                gridTemplateColumns: row.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                borderTop: rowIdx > 0 ? `1px solid ${ap.cardBorder}` : undefined,
              }}
            >
              {row.map((field, colIdx) => (
                <AssetFieldCell
                  key={field.label}
                  field={field}
                  c={c}
                  ap={ap}
                  showLeftBorder={colIdx > 0}
                />
              ))}
            </div>
          ))}
        </div>

        {commandState && (
          <AssetCommandsSection
            assetId={asset.id}
            commands={commandState.commandsForAsset(asset.id)}
            loading={commandState.loading}
            saving={commandState.saving}
            addCommand={commandState.addCommand}
            removeCommand={commandState.removeCommand}
          />
        )}
      </div>

      <DeleteConfirmModal {...deleteModalProps} />
    </div>
  );
}

function AssetFieldCell({ field, c, ap, showLeftBorder }) {
  const icon = ICON_MAP[field.icon] ?? Ico.model;
  return (
    <div style={{ borderLeft: showLeftBorder ? `1px solid ${ap.cardBorder}` : undefined }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", minWidth: 0 }}>
        <div style={{ width: 26, height: 26, borderRadius: 6, background: ap.fieldIconBg, border: `1px solid ${ap.fieldIconBorder}`, display: "flex", alignItems: "center", justifyContent: "center", color: ap.fieldIconColor, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: ap.fieldLabel, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{field.label}</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: c.text, wordBreak: "break-word", lineHeight: 1.35 }}>{field.value || "—"}</div>
        </div>
      </div>
    </div>
  );
}
